/**
 * AUDIT — a learner audits a real ANKR product against STATE, TRUST, SENSE, PROOF.
 *
 * FOUNDER, 2026-08-15: "build p2 and p4".
 *
 * ── THE PAGE SAYS THE GRADING LAW BEFORE IT ASKS ANYTHING ─────────────────────────────────────
 * "Reporting that a product FAILS a declaration is a pass." A learner who does not know that will
 * hedge, and a hedged audit is worthless. So it is the first thing on the page, not a footnote —
 * because the whole exercise turns on believing it.
 *
 * ── OUR OWN FINDING IS HIDDEN UNTIL THEIRS IS MADE ────────────────────────────────────────────
 * ANKR's audit of the same product is published, but behind a control that will not open until the
 * learner has recorded all four of their own. Showing it first would make this a reading exercise.
 * And when it does open, it opens as a COMPARISON, never as a correction: a difference is a thing
 * to look into, in either direction.
 */
import { useState } from 'react';
import { Value } from '../../../packages/ankr-glass/src/react.jsx';
import { computed, cited, refused } from '../../../packages/ankr-provenance/src/provenance.mjs';
import SUBJECTS from '../../audit-subjects.json';
import {
  DECLARATIONS, VERDICTS, subjects, subjectFor, checkAudit, compareWithOurs,
  NEGATIVE_IS_A_PASS, NOT_AN_ANSWER_KEY,
} from './auditspine.mjs';

const STORE = 'ankr-academy-audit';
const load = () => { try { return JSON.parse(localStorage.getItem(STORE) || '{}'); } catch { return {}; } };
const save = (a) => { try { localStorage.setItem(STORE, JSON.stringify(a)); } catch { /* not worth breaking the page */ } };

export default function AuditLab() {
  const all = subjects(SUBJECTS);
  const [id, setId] = useState(all[0]?.id ?? null);
  const [audit, setAudit] = useState(() => load());
  const [reveal, setReveal] = useState(false);
  const subj = subjectFor(SUBJECTS, id);
  const mine = audit[id] || { findings: {} };
  const c = checkAudit(mine);

  const record = (decl, patch) => {
    const next = { ...audit, [id]: { ...mine, findings: { ...mine.findings, [decl]: { ...(mine.findings[decl] || {}), ...patch } } } };
    setAudit(next); save(next);
  };

  return (
    <div className="g-glass shell-panel lab">
      <div className="g-label">Audit · a real ANKR product</div>
      <h2 className="g-h2 lab-h">Find what it does not declare</h2>

      <p className="g-body aud-law"><b>{NEGATIVE_IS_A_PASS}</b></p>

      <div className="lab-task-head">
        <span className="g-label">Subject</span>
        {all.map((s) => (
          <button key={s.id} className={`g-btn ${id === s.id ? 'g-btn-primary' : ''}`}
            onClick={() => { setId(s.id); setReveal(false); }}>{s.name}</button>
        ))}
      </div>
      {subj && (
        <p className="g-body g-dim">
          {subj.what_it_is} <b>Where to look:</b> {subj.where_to_look}
        </p>
      )}

      {/* ── THE FOUR ── */}
      <div className="lab-task">
        <div className="g-label">Examine all four. Say why, in each case.</div>
        {DECLARATIONS.map((d) => {
          const f = mine.findings[d.id] || {};
          return (
            <div key={d.id} className={`aud-decl ${f.verdict ? 'aud-set' : ''}`}>
              <div className="aud-head"><b>{d.name}</b> <span className="g-dim">— {d.asks}</span></div>
              <div className="lab-task-head">
                {VERDICTS.map((v) => (
                  <button key={v.id} className={`g-btn ${f.verdict === v.id ? 'g-btn-primary' : ''}`}
                    title={v.means} onClick={() => record(d.id, { verdict: v.id })}>{v.label}</button>
                ))}
              </div>
              <textarea className="prj-ta aud-why" rows={2} value={f.why || ''}
                placeholder="Why did you conclude that? What did you look at?"
                onChange={(e) => record(d.id, { why: e.target.value })} />
            </div>
          );
        })}
      </div>

      {/* ── THE CHECK ── */}
      <div className="lab-task">
        <div className="g-label">The check — published before you started</div>
        <div className={`prj-verdict ${c.passes ? 'prj-pass' : ''}`}>
          {c.passes ? <Value of={computed(c.verdict)} /> : <Value of={refused(c.verdict)} />}
        </div>
        {!c.passes && (
          <ul className="fai-list">
            {[...c.missing, ...c.unreasoned].map((m) => <li key={m.id}>{m.asks}</li>)}
          </ul>
        )}
        {c.passes && (
          <p className="g-body">
            Four examined, four reasons given. Note what was <i>not</i> checked: whether your findings
            were favourable. An audit reporting four failures passes exactly as this one did.
          </p>
        )}
      </div>

      {/* ── OURS, ONLY AFTER THEIRS ── */}
      <div className="lab-task">
        <div className="g-label">What we found, when you are ready to compare</div>
        {!c.passes ? (
          <p className="g-body g-dim">
            Locked until all four of yours are recorded. Reading ours first would make this a
            reading exercise.
          </p>
        ) : !reveal ? (
          <button className="g-btn g-btn-primary" onClick={() => setReveal(true)}>Show ANKR's own finding</button>
        ) : (
          <>
            <p className="g-body g-dim">{NOT_AN_ANSWER_KEY}</p>
            {compareWithOurs(subj, mine).map((r) => (
              <div key={r.id} className={`aud-cmp ${r.differs ? 'aud-differs' : ''}`}>
                <div className="aud-head"><b>{r.name}</b>{r.differs && <span className="aud-flag">you differ</span>}</div>
                <p className="g-body"><b>You:</b> {VERDICTS.find((v) => v.id === r.mine)?.label ?? '—'}</p>
                <p className="g-body g-dim"><b>Us:</b> {r.ours}</p>
              </div>
            ))}
            <p className="g-body aud-note">
              A difference is a thing to look into, in either direction. Nothing here scores against
              ours, and a learner who finds something we missed has done this better than we did.
            </p>
          </>
        )}
      </div>

      <p className="g-body g-dim lab-why">
        <Value of={cited('the four declarations', 'CodeAI101 module 06 §1')} /> — examined against a
        real, live system rather than an invented one, because auditing something made up would be
        speech about speech.
        <br />
        Which products appear here is a founder disclosure decision, not an engineering one. This
        page reads a declared list and adds nothing to it. It ships with one subject chosen because
        it needs no new disclosure: the tutor you are sitting in, whose own audit is already
        published inside CodeAI101 chapter 6.
      </p>
    </div>
  );
}
